import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Me } from "shared";
import { api, setCsrf } from "./api";

type Ctx = { me: Me | null; loading: boolean; refresh: () => Promise<void>; signOut: () => Promise<void> };
const AuthCtx = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Every way out of a session drops cached data (cases, customers), so the next sign-in on this browser starts empty.
  const forget = useCallback(() => { setCsrf(null); setMe(null); queryClient.clear(); }, [queryClient]);

  const refresh = useCallback(async () => {
    try {
      const m = await api<Me>("/api/auth/me");
      setCsrf(m.csrf);
      setMe(m);
    } catch {
      forget();
    } finally {
      setLoading(false);
    }
  }, [forget]);

  const signOut = useCallback(async () => {
    try { await api("/api/auth/logout", { method: "POST" }); } finally { forget(); }
  }, [forget]);

  useEffect(() => {
    refresh();
    window.addEventListener("afhh:signed-out", forget);
    return () => window.removeEventListener("afhh:signed-out", forget);
  }, [refresh, forget]);

  return <AuthCtx.Provider value={{ me, loading, refresh, signOut }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): Ctx {
  const c = useContext(AuthCtx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}
